'use client';
import { BlockedUserError } from '@/lib/authentication';
import { $fetchAllo, isApiError } from '@/services/api-allo/client'; // Assuming this path is resolvable
import { getCallDiagnosticsSync } from '@/services/logger/call-diagnostics';
import { createLogger, logger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import VonageClient, { type ClientInitConfigObject, ConfigRegion } from '@vonage/client-sdk';
import {
  createContext,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  type SessionErrorContext,
  type SessionTrigger,
  type VonageSessionManager,
  createVonageSessionManager,
} from './vonage-session-manager';

export type { SessionTrigger } from './vonage-session-manager';

export const VonageRegion = {
  US: ConfigRegion.US,
  EU: ConfigRegion.EU,
  AP: ConfigRegion.AP,
};

const log = createLogger('VonageClientProvider', ['calls', 'vonage']);

// ============================================================================
// Module-level VonageClient singleton.
//
// The SDK is designed as a long-lived singleton. Owning it at module scope
// prevents `router.invalidate()` (auth flows, impersonation) from minting a
// new VonageClient and orphaning every in-flight callId — the root cause of
// phantom calls. See specs/vonage-client-lifetime.md for the full analysis.
//
// Lifetime: one instance per Electron renderer process. Explicit resets are
// required for SPA-level auth-user changes (logout, stop-impersonation).
// START-impersonation at src/routes/_app/internal/users.tsx:116 does
// `window.location.href = '/'` (hard reload) which destroys this singleton
// for free; do NOT SPA-ify that flow without adding an explicit reset here.
// ============================================================================

let instance: VonageClient | null = null;
let instanceCount = 0;
let lastResetReason: string | null = null;

type ResetReason = 'logout' | 'user-change' | 'impersonation-stop' | 'region-change';

export const getVonageClient = (config?: ClientInitConfigObject): VonageClient => {
  if (instance) {
    return instance;
  }

  if (!config) {
    throw new Error('[VonageClient] getVonageClient called without config before initialization');
  }

  instance = new VonageClient(config);
  instanceCount += 1;

  // `instanceCount > 1` without a matching resetReason is a regression:
  // something re-instantiated the client without going through resetVonageClient().
  const isExpected = instanceCount === 1 || lastResetReason !== null;
  const instantiatedContext = {
    instanceCount,
    resetReason: lastResetReason,
    timestamp: Date.now(),
  };
  if (isExpected) {
    log.info('Vonage client instantiated', instantiatedContext);
  } else {
    log.error('Vonage client instantiated', instantiatedContext);
  }
  lastResetReason = null;

  return instance;
};

export const resetVonageClient = (reason: ResetReason): void => {
  instance?.deleteSession?.()?.catch(() => {});
  instance = null;
  setCurrentSessionId(undefined);
  // Clear user context so SDK logs that fire during teardown (e.g. from
  // deleteSession) don't carry the previous user's identity.
  currentUserContext = null;
  // Reset phantom-diagnostic counters — the counters are per-authenticated-user
  // and shouldn't carry across logout/impersonation-stop.
  totalSessionCreateCount = 0;
  lastSessionCreateAt = null;
  sessionCreateCountAtLastCallStart = 0;
  // Clear the media-timeout rolling window too. Without this, stale timestamps
  // from the previous user survive a logout/login within the 2-minute window
  // and can trigger a spurious `media-timeout-burst` manual-reset after the
  // next timeout on the newly-logged-in user.
  mediaTimeoutTimestamps.length = 0;
  // Defensive: if a reset happens while a call is still dispatched (logout
  // during a live call), clear the active-call marker so a stale value can't
  // gate the heartbeat for the next user's session.
  activeVonageCallId = undefined;
  callStartingCount = 0;
  // A reset invalidates any in-flight refresh — the session it was building
  // is gone. Clearing the promise lets the next user's `ensureSessionReady`
  // return immediately rather than awaiting a promise whose resolution is
  // no longer meaningful.
  currentRefreshPromise = null;
  // Tear down any active reconnect grace — the call/session it was holding
  // open is gone, and the previous user's destructive cleanup must not fire
  // against the next user's state.
  tearDownReconnectGrace();
  lastResetReason = reason;
};

// ============================================================================
// Module-level current session id.
//
// Moving sessionId out of the provider's ref/prop and into module scope
// removes the R3 foot-gun (the prop-sync useEffect could clobber a good
// sessionId when the loader returned undefined) and lets `refreshVonageClient`
// short-circuit on loader re-runs when a session already exists — which is
// what stops `router.invalidate()` from rotating the Vonage session.
//
// Reactivity: the provider subscribes via `useSyncExternalStore` so every
// write to currentSessionId propagates to every `useVonageSession()` consumer.
// ============================================================================

let currentSessionId: string | undefined;
const sessionIdSubscribers = new Set<() => void>();

const getCurrentSessionIdSnapshot = (): string | undefined => currentSessionId;

const subscribeSessionId = (onChange: () => void): (() => void) => {
  sessionIdSubscribers.add(onChange);
  return () => {
    sessionIdSubscribers.delete(onChange);
  };
};

const setCurrentSessionId = (id: string | undefined): void => {
  if (currentSessionId === id) {
    return;
  }
  currentSessionId = id;
  // Mirror onto the tracing singleton so call logs can tag every New Relic
  // entry with `vonageSessionId` without depending on the React tree.
  tracingService.setVonageSessionId(id ?? null);
  for (const cb of sessionIdSubscribers) {
    cb();
  }
};

// ============================================================================
// Module-level user context.
//
// The VonageClient's `customLoggers` used to capture `user` / `businessData`
// by closure at construction time. With the client now a singleton, those
// captures would go stale after any SPA-level user change (logout,
// stop-impersonation) — every SDK log event would still be tagged to
// whichever user was active at initial construction.
//
// Fix: the loader writes the active user's identity here on every run via
// `setVonageUserContext`, and the customLoggers read it lazily via
// `getVonageUserContext()` at log-time. `resetVonageClient` clears the
// context alongside the instance so a stale read after logout returns
// `null` rather than the previous user's data.
// ============================================================================

export type VonageUserContext = {
  userId: string;
  userEmail?: string | null;
  userPhone?: string | null;
  businessId?: string | null;
  region?: string;
};

let currentUserContext: VonageUserContext | null = null;

export const setVonageUserContext = (context: VonageUserContext): void => {
  currentUserContext = context;
};

export const getVonageUserContext = (): VonageUserContext | null => currentUserContext;

// ============================================================================
// Module-level refresh dedup + session-ready barrier.
//
// `currentRefreshPromise` tracks whether any of the refresh paths (heartbeat,
// session-error auto-refresh, media-timeout burst, banner/manual refresh)
// is in flight. It lives at module scope because:
//   (1) All paths must dedup against each other — two refreshes running
//       concurrently would tear down each other's websocket.
//   (2) Call-initiation sites (use-server-call, use-incoming-call) need to
//       wait on it *before* invoking `serverCall()`/`answer()`. Our
//       `createSession(token, previousId)` resolves when the session is
//       created at the API layer, but the SDK's websocket then enters a
//       5-second `startVerifyResponseTimer` handshake window. If the user
//       clicks Call during that window, `serverCall()` throws
//       `SessionNoActiveSessionError` and `withRetry` retries with no
//       delay — both attempts land inside the handshake window. See
//       `ensureSessionReady()` below.
// ============================================================================

/**
 * Outcome shape carried by the in-flight refresh promise. Dedup callers use
 * this to distinguish success (reuse the id) from failure (fall through and
 * run their own work). Before this was introduced, the barrier was a void
 * promise and dedup callers inferred success from `currentSessionId` being
 * truthy — which silently returned the stale id when a resume failed against
 * an expired previousSessionId.
 */
type RefreshOutcome = { ok: true; sessionId: string | undefined } | { ok: false; error: unknown };

let currentRefreshPromise: Promise<RefreshOutcome> | null = null;

export const getCurrentRefreshPromise = (): Promise<RefreshOutcome> | null => currentRefreshPromise;

const setCurrentRefreshPromise = (promise: Promise<RefreshOutcome> | null): void => {
  currentRefreshPromise = promise;
};

// Timeout for `ensureSessionReady` callers to wait on an in-flight refresh.
// Covers the 5s Vonage SDK `startVerifyResponseTimer` + ~1s buffer for token
// fetch latency. Shorter values (e.g. 2s) leak the exact reconnect window we
// are trying to cover; longer values risk hanging the UI if a refresh stalls.
const ENSURE_SESSION_READY_TIMEOUT_MS = 6000;

/**
 * Await any in-flight session refresh before the caller invokes an SDK
 * operation that requires an active session (`serverCall`, `answer`, etc.).
 *
 * Resolves immediately if no refresh is pending — the caller is then free
 * to invoke the SDK, and any transient `SessionNoActiveSessionError` should
 * be handled at the retry layer with a delay (see `use-server-call` and
 * `use-incoming-call`).
 *
 * Caps the wait at `ENSURE_SESSION_READY_TIMEOUT_MS`. On timeout, resolves
 * silently so the caller can attempt the SDK operation anyway — surfacing
 * the SDK's own error is more useful than a synthetic timeout error.
 */
export const ensureSessionReady = async (): Promise<void> => {
  const pending = currentRefreshPromise;
  if (!pending) {
    return;
  }
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timeoutId = setTimeout(resolve, ENSURE_SESSION_READY_TIMEOUT_MS);
  });
  try {
    // `pending` never rejects — the refresh barrier wraps its work so the
    // outcome is carried as a resolved value. We only need to know when it
    // settles, so the result is discarded.
    await Promise.race([pending, timeout]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
};

// ============================================================================
// Module-level active-call marker.
//
// The TTL heartbeat uses resume semantics (`createSession(token, previousId)`)
// to stay ahead of the 15-min server TTL. Even a resume tears down and
// re-establishes the SDK's websocket, which can race with an in-flight
// call's SDP negotiation — we saw this in prod as
// `sh: No session ID found in store` + `Inbound call failed` when a 60s
// heartbeat tick happened to coincide with a user answering an incoming call.
//
// This flag lets the heartbeat defer while a call is active. The voice layer
// (VoiceCallStateProvider) writes this on every reducer transition; everything
// else reads via `getVonageActiveCallId()`.
// ============================================================================

let activeVonageCallId: string | undefined;

export const setVonageActiveCallId = (callId: string | undefined): void => {
  activeVonageCallId = callId;
};

export const getVonageActiveCallId = (): string | undefined => activeVonageCallId;

// ============================================================================
// Module-level call-starting marker.
//
// The active-call marker above is driven by the reducer: it only flips on
// once `callId` is set (after `serverCall()` / `answer()` resolves). During
// the `initiating` → `initiated` window the call-initiation hook has
// already committed to starting a call but has no callId yet, so the
// marker is still undefined and the heartbeat doesn't defer.
//
// `callStartingCount` closes that window. Call-initiation hooks bump it
// synchronously (before `await ensureSessionReady()`) and decrement in a
// `finally` — so even if two hooks happen to overlap (e.g. rapid answer
// after hangup), the heartbeat stays deferred until *all* initiation
// windows close.
// ============================================================================

let callStartingCount = 0;

export const markCallStarting = (): void => {
  callStartingCount += 1;
};

export const unmarkCallStarting = (): void => {
  callStartingCount = Math.max(0, callStartingCount - 1);
};

export const isCallStarting = (): boolean => callStartingCount > 0;

// ============================================================================
// Phantom-call diagnostic counters.
//
// The `call:phantom:detected` event (emitted from useVerifiedHangup when a
// server-side or privacy fallback resolves a leg) joins the symptom
// (verified-hangup flow recovered from a stuck hangup) to the cause
// (was the client re-instantiated? was the session rotated mid-call?).
//
// After Phases A-D ship, `instanceCount` should always be 1 and
// `sessionsSinceCallStart` should usually be 0 when a phantom occurs. Any
// `call:phantom:detected` with instanceCount > 1 or sessionsSinceCallStart > 0
// is an unfixed regression and points at which phase regressed.
// ============================================================================

let totalSessionCreateCount = 0;
let lastSessionCreateAt: number | null = null;
let sessionCreateCountAtLastCallStart = 0;

export const getVonageInstanceCount = (): number => instanceCount;

export type VonageSessionDiagnostics = {
  instanceCount: number;
  totalSessionCreateCount: number;
  sessionsSinceCallStart: number;
  timeSinceLastSessionCreateMs: number | null;
};

export const getVonageSessionDiagnostics = (): VonageSessionDiagnostics => ({
  instanceCount,
  totalSessionCreateCount,
  sessionsSinceCallStart: totalSessionCreateCount - sessionCreateCountAtLastCallStart,
  timeSinceLastSessionCreateMs:
    lastSessionCreateAt === null ? null : Date.now() - lastSessionCreateAt,
});

/**
 * Snapshot the session-create counter at the start of a call so the verified
 * hangup flow can later tell how many session rotations happened *during*
 * this call's lifetime. Called from voice hooks on call-initiated and
 * call-invite-answered dispatches.
 */
export const markVonageCallStart = (): void => {
  sessionCreateCountAtLastCallStart = totalSessionCreateCount;
};

const recordSessionCreated = (): void => {
  totalSessionCreateCount += 1;
  lastSessionCreateAt = Date.now();
};

// ============================================================================
// Media-timeout burst tracker.
//
// Single media timeouts no longer rotate the Vonage session (that's the R4
// amplifier loop from specs/vonage-client-lifetime.md). Instead, voice hooks
// report each media timeout via `recordMediaTimeout()`. If ≥N timeouts land
// in a rolling window, we trigger exactly one controlled `manual-reset`
// refresh to recover from genuinely corrupted SDK state.
//
// Tuning numbers are a starting point — adjust against NR `call:media-timeout`
// baselines after deploy.
// ============================================================================

const MEDIA_TIMEOUT_WINDOW_MS = 2 * 60 * 1000;
const MEDIA_TIMEOUT_BURST_THRESHOLD = 3;
const mediaTimeoutTimestamps: number[] = [];

export const recordMediaTimeout = (): void => {
  const now = Date.now();
  mediaTimeoutTimestamps.push(now);
  while (
    mediaTimeoutTimestamps.length > 0 &&
    (mediaTimeoutTimestamps[0] ?? 0) < now - MEDIA_TIMEOUT_WINDOW_MS
  ) {
    mediaTimeoutTimestamps.shift();
  }

  if (mediaTimeoutTimestamps.length < MEDIA_TIMEOUT_BURST_THRESHOLD) {
    return;
  }

  log.error('Media timeout burst', {
    rollingWindowCount: mediaTimeoutTimestamps.length,
    windowMs: MEDIA_TIMEOUT_WINDOW_MS,
    threshold: MEDIA_TIMEOUT_BURST_THRESHOLD,
    timestamp: now,
  });
  tracingService.trackEvent(CallEventName.CallMediaTimeoutBurst, CallEventLevel.Error, {
    rollingWindowCount: mediaTimeoutTimestamps.length,
    windowMs: MEDIA_TIMEOUT_WINDOW_MS,
    threshold: MEDIA_TIMEOUT_BURST_THRESHOLD,
  });

  // Clear so one burst fires exactly one reset; subsequent timeouts start
  // counting from zero.
  mediaTimeoutTimestamps.length = 0;

  refreshVonageClient({
    trigger: { type: 'manual-reset', reason: 'media-timeout-burst' },
  }).catch((error) => {
    logger.error('[VonageClientProvider] media-timeout-burst refresh failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  });
};

/**
 * Handler function called when a Vonage session error occurs.
 * Used by components that need to cleanup state when the session becomes invalid.
 *
 * @example
 * ```typescript
 * registerSessionErrorHandler(async () => {
 *   // Cleanup call state, reset UI, etc.
 * });
 * ```
 */
type SessionErrorHandler = () => void | Promise<void>;

type VonageClientContextType = {
  client: VonageClient;
  sessionId: string | undefined;
  isAvailable: boolean; // True if Vonage session is connected and ready for calls
  refreshSession: () => Promise<void>;
  isConnectionBlocked: boolean;
  resetConnection: () => void;
  /**
   * Register a handler to be called when session errors occur.
   * This implements the Observer pattern - when a session error happens,
   * all registered handlers are notified so they can cleanup their state.
   *
   * @param handler - Function to call on session error
   * @returns Cleanup function to unregister the handler
   */
  registerSessionErrorHandler: (handler: SessionErrorHandler) => () => void;
};

// Vonage's server-side session TTL is ~15 minutes. The SDK's WebSocket
// keepalive extends it while the tab is active, but backgrounded tabs
// (throttled timers) and system sleep can let the session expire silently —
// the next `serverCall` then returns 401 and the SDK surfaces it as a
// generic network failure (manifests as "CORS error" in DevTools because
// the 401 response carries no Access-Control-Allow-Origin header).
//
// Refresh at 10 min (~2/3 of TTL) gives margin for clock skew and slow
// networks. Check cadence is 60s — below that, Chrome's background-tab
// throttle floor means the effective interval is the same anyway, and the
// visibilitychange listener picks up the case where a deeply-throttled
// tab was skipped and just returned to foreground.
const SESSION_TTL_HEARTBEAT_THRESHOLD_MS = 10 * 60 * 1000;
const SESSION_TTL_HEARTBEAT_INTERVAL_MS = 60 * 1000;

// Reconnect grace — runs after a sessionError auto-refresh has already
// failed. The SDK's WebRTC media often stays alive across signaling
// failures, so we hold the destructive cleanup for one bounded recovery
// window instead of tearing down the call as soon as the refresh rejects.
//
// Recovery loop: immediate retry + 1s/2s/4s/8s scheduled ladder, capped by
// the window. `online` and `visibilitychange` fire supplemental retries to
// catch sleep/wake and tab-return cases the timer would miss. First success
// stops the loop; window expiry runs the original destructive handlers as a
// last resort so the UI doesn't lie about call state forever when the
// session truly is gone.

const SESSION_RECONNECT_WINDOW_MS = 30_000;
const RECONNECT_RETRY_GAPS_MS = [1_000, 2_000, 4_000, 8_000];
const MIN_RECOVERY_ATTEMPT_GAP_MS = 1_000;

// Bumped on every `enterReconnectGrace` and on every `tearDownReconnectGrace`.
// Each in-flight attempt captures the id at call time and re-checks it in
// `.then`/`.catch`/`.finally`; a late resolve from a torn-down window
// compare-fails and bails out. Without this, a 4s `attemptRecovery` that
// resolves at t+31s could emit `reconnect-grace-recovered` after the
// give-up handler already ran — or worse, flip the in-flight flag inside a
// freshly-started next grace.
let reconnectWindowId = 0;
let reconnectGraceTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectRetryTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectOnlineHandler: (() => void) | null = null;
let reconnectVisibilityHandler: (() => void) | null = null;
let reconnectAttemptInFlight = false;
let reconnectLastTryAt = 0;

const tearDownReconnectGrace = (): void => {
  reconnectWindowId += 1;
  if (reconnectGraceTimer) {
    clearTimeout(reconnectGraceTimer);
    reconnectGraceTimer = null;
  }
  if (reconnectRetryTimer) {
    clearTimeout(reconnectRetryTimer);
    reconnectRetryTimer = null;
  }
  if (reconnectOnlineHandler && typeof window !== 'undefined') {
    window.removeEventListener('online', reconnectOnlineHandler);
    reconnectOnlineHandler = null;
  }
  if (reconnectVisibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', reconnectVisibilityHandler);
    reconnectVisibilityHandler = null;
  }
  reconnectAttemptInFlight = false;
  reconnectLastTryAt = 0;
};

// Captured at entry and stamped on every telemetry event so cohort queries
// can split recovered/expired by SDK error reason and by call phase at entry.
type ReconnectGraceMeta = {
  reason: string;
  triggerType: 'expired-token' | 'transport-closed' | 'ping-timeout';
  previousSessionId: string | null;
  getCallIdAtEntry: () => string | undefined;
};

type RecoveryCause = 'initial' | 'scheduled' | 'online' | 'visibility';

const enterReconnectGrace = (
  attemptRecovery: () => Promise<string | undefined>,
  onGiveUp: () => void | Promise<void>,
  meta: ReconnectGraceMeta,
): void => {
  if (reconnectGraceTimer) {
    return;
  }

  const startedAt = Date.now();
  const callIdAtEntry = meta.getCallIdAtEntry();
  // `isCallStarting()` covers the initiating→initiated window before
  // `serverCall()`/`answer()` assigns a callId. A sessionError that lands in
  // that window still has a call worth preserving even though
  // `getVonageActiveCallId()` returns undefined.
  const callStarting = isCallStarting();
  const hasCallInFlight = !!callIdAtEntry || callStarting;
  const baseMeta = {
    reason: meta.reason,
    triggerType: meta.triggerType,
    previousSessionId: meta.previousSessionId,
    callIdAtEntry: callIdAtEntry ?? null,
    hadActiveCall: !!callIdAtEntry,
    callStartingAtEntry: callStarting,
  };

  if (!hasCallInFlight) {
    log.info('Reconnect grace skipped', {
      ...baseMeta,
      skipReason: 'no-active-call',
      timestamp: startedAt,
    });
    void onGiveUp();
    return;
  }

  const windowId = ++reconnectWindowId;
  let attempts = 0;
  let lastSuccessfulCause: RecoveryCause | null = null;

  log.warn('Reconnect grace started', {
    ...baseMeta,
    windowMs: SESSION_RECONNECT_WINDOW_MS,
    retryGapsMs: RECONNECT_RETRY_GAPS_MS,
    timestamp: startedAt,
  });
  tracingService.trackEvent(
    CallEventName.VonageSessionReconnectGraceStart,
    CallEventLevel.Warning,
    {
      deviceLegId: callIdAtEntry ?? undefined,
      reason: meta.reason,
      triggerType: meta.triggerType,
      previousSessionId: meta.previousSessionId,
      hadActiveCall: !!callIdAtEntry,
      callStartingAtEntry: callStarting,
      windowMs: SESSION_RECONNECT_WINDOW_MS,
    },
  );

  const isCurrentWindow = (): boolean => reconnectWindowId === windowId;

  const tryOnce = (cause: RecoveryCause): void => {
    if (!isCurrentWindow()) {
      return;
    }
    if (reconnectAttemptInFlight) {
      return;
    }
    const now = Date.now();
    if (now - reconnectLastTryAt < MIN_RECOVERY_ATTEMPT_GAP_MS) {
      return;
    }

    reconnectLastTryAt = now;
    reconnectAttemptInFlight = true;
    attempts += 1;
    const attemptStart = now;
    const attemptIdx = attempts;

    attemptRecovery()
      .then((sid) => {
        if (!isCurrentWindow() || !sid || lastSuccessfulCause) {
          return;
        }
        lastSuccessfulCause = cause;
        const durationMs = Date.now() - startedAt;
        const callIdAtRecovery = getVonageActiveCallId();
        logger.info('[VonageClientProvider] Reconnect grace recovered', {
          ...baseMeta,
          sessionId: sid,
          attempts,
          durationMs,
          cause,
        });
        log.info('Reconnect grace recovered', {
          ...baseMeta,
          sessionId: sid,
          attempts,
          durationMs,
          cause,
          callIdAtRecovery: callIdAtRecovery ?? null,
          callIdPreserved: !!callIdAtEntry && callIdAtEntry === callIdAtRecovery,
          timestamp: Date.now(),
        });
        tracingService.trackEvent(
          CallEventName.VonageSessionReconnectGraceRecovered,
          CallEventLevel.Info,
          {
            deviceLegId: callIdAtRecovery ?? callIdAtEntry ?? undefined,
            reason: meta.reason,
            triggerType: meta.triggerType,
            previousSessionId: meta.previousSessionId,
            sessionId: sid,
            attempts,
            durationMs,
            cause,
            callIdPreserved: !!callIdAtEntry && callIdAtEntry === callIdAtRecovery,
          },
        );
        tearDownReconnectGrace();
      })
      .catch((error) => {
        if (!isCurrentWindow()) {
          return;
        }
        logger.warn('[VonageClientProvider] Reconnect grace retry failed', {
          ...baseMeta,
          attempt: attemptIdx,
          cause,
          attemptDurationMs: Date.now() - attemptStart,
          error: error instanceof Error ? error.message : String(error),
        });
      })
      .finally(() => {
        // Only the owning window may release the flag. Otherwise a stale
        // promise from a torn-down window could clear an in-flight flag
        // that the new window's own `tryOnce` has just set.
        if (!isCurrentWindow()) {
          return;
        }
        reconnectAttemptInFlight = false;
      });
  };

  const scheduleRetry = (idx: number): void => {
    if (!isCurrentWindow() || idx >= RECONNECT_RETRY_GAPS_MS.length) {
      return;
    }
    const gap = RECONNECT_RETRY_GAPS_MS[idx];
    if (gap === undefined) {
      return;
    }
    reconnectRetryTimer = setTimeout(() => {
      reconnectRetryTimer = null;
      tryOnce('scheduled');
      scheduleRetry(idx + 1);
    }, gap);
  };

  reconnectOnlineHandler = () => {
    if (typeof navigator === 'undefined' || navigator.onLine) {
      tryOnce('online');
    }
  };
  reconnectVisibilityHandler = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      tryOnce('visibility');
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', reconnectOnlineHandler);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', reconnectVisibilityHandler);
  }

  reconnectGraceTimer = setTimeout(() => {
    if (!isCurrentWindow()) {
      return;
    }
    const callIdAtExpiry = getVonageActiveCallId();
    logger.error('[VonageClientProvider] Reconnect grace window expired, running cleanup', {
      ...baseMeta,
      attempts,
    });
    log.error('Reconnect grace expired', {
      ...baseMeta,
      attempts,
      windowMs: SESSION_RECONNECT_WINDOW_MS,
      callIdAtExpiry: callIdAtExpiry ?? null,
      callStillActive: !!callIdAtExpiry,
      timestamp: Date.now(),
    });
    tracingService.trackEvent(
      CallEventName.VonageSessionReconnectGraceExpired,
      CallEventLevel.Error,
      {
        deviceLegId: callIdAtExpiry ?? callIdAtEntry ?? undefined,
        reason: meta.reason,
        triggerType: meta.triggerType,
        previousSessionId: meta.previousSessionId,
        attempts,
        windowMs: SESSION_RECONNECT_WINDOW_MS,
        callStillActive: !!callIdAtExpiry,
      },
    );
    tearDownReconnectGrace();
    void onGiveUp();
  }, SESSION_RECONNECT_WINDOW_MS);

  // Without the immediate attempt, a user already-online with the tab
  // focused gets no retries — `online`/`visibilitychange` only fire on
  // transitions, and the ladder ticks 1s later.
  tryOnce('initial');
  scheduleRetry(0);
};

export const VonageClientContext = createContext<VonageClientContextType>(
  {} as VonageClientContextType,
);

type VonageClientProviderProps = {
  children: React.ReactNode;
};

export const sessionManager: VonageSessionManager = createVonageSessionManager({
  maxRetries: 3,
  maxFailures: 5,
  blockDuration: 60000,
  logger: {
    info: (message: string, meta?: Record<string, unknown>) => {
      logger.info(message, { ...meta, timestamp: Date.now() });
    },
    warn: (message: string, meta?: Record<string, unknown>) => {
      logger.warn(message, { ...meta, timestamp: Date.now() });
    },
    error: (message: string, meta?: Record<string, unknown>) => {
      logger.error(message, { ...meta, timestamp: Date.now() });
    },
  },
  onError: (context: SessionErrorContext) => {
    const diagnostics = getCallDiagnosticsSync();
    // `connection_blocked` means the breaker short-circuited this call — the
    // underlying failure was already logged at ERROR when the breaker tripped.
    // Logging again at ERROR here produces the 3-event-per-retry-cycle Sentry
    // churn we saw in 2.69.x. Downgrade to WARN so a persistent bad-auth
    // backend state stays one Sentry group rather than spawning new ones.
    const isBlockedShortCircuit = context.errorType === 'connection_blocked';
    const logFn = isBlockedShortCircuit ? logger.warn : logger.error;
    logFn(`Vonage session ${context.operation} error`, {
      ...context,
      diagnostics,
      timestamp: Date.now(),
    });

    // "Retries exhausted" is meaningful only when retries were actually
    // budgeted and consumed. The connection-blocked short-circuit sets
    // `attempt: 0, maxRetries: 0`, so the old `attempt >= maxRetries` check
    // fired this ERROR on every short-circuit — duplicate noise.
    if (!isBlockedShortCircuit && context.maxRetries > 0 && context.attempt >= context.maxRetries) {
      logger.error(`Vonage session ${context.operation} retries exhausted`, {
        maxRetries: context.maxRetries,
        errorType: context.errorType,
        totalDuration: context.duration,
        errorMessage: context.errorMessage,
        timestamp: Date.now(),
        diagnostics,
      });
    }
  },
  onConnectionBlocked: () => {
    logger.error('Vonage session connection blocked', {
      timestamp: Date.now(),
      diagnostics: getCallDiagnosticsSync(),
    });
  },
  onConnectionRestored: () => {
    logger.info('Vonage session connection restored', {
      timestamp: Date.now(),
    });
  },
});

export const VonageClientProvider = ({ children }: VonageClientProviderProps) => {
  // The singleton must have been initialized by the loader before the provider
  // renders. `getVonageClient()` without a config throws if it hasn't been.
  const client = getVonageClient({ rtcStatsTelemetry: true });
  // sessionId is module-level state (see setCurrentSessionId).
  // useSyncExternalStore keeps the provider's render tree in sync with it.
  // This replaces the prop + ref + useEffect pattern, which was the R3
  // foot-gun: a loader that returned undefined (error branch) would clobber
  // a previously-good ref.
  const sessionId = useSyncExternalStore(subscribeSessionId, getCurrentSessionIdSnapshot);
  const [connectionBlocked, setConnectionBlocked] = useState(false);
  const sessionErrorHandlersRef = useRef<Set<SessionErrorHandler>>(new Set());
  const lastSessionErrorRef = useRef<{ reason: string; timestamp: number } | null>(null);

  useEffect(() => {
    const checkConnectionHealth = () => {
      const state = sessionManager.getConnectionState();
      setConnectionBlocked(state.isBlocked);
    };

    const interval = setInterval(checkConnectionHealth, 5000);
    checkConnectionHealth();

    return () => clearInterval(interval);
  }, []);

  // Session TTL heartbeat. Proactively resume the Vonage session when it
  // approaches the server-side TTL so a foregrounded long-idle tab doesn't
  // lose the next call to a 401. Uses resume semantics (carries the
  // previousSessionId) so active calls aren't dropped. Shares
  // `currentRefreshPromise` (module state) with the session-error auto-refresh
  // path below so the two can't double-fire against each other, and with
  // `ensureSessionReady()` so call-initiation sites can await an in-flight
  // refresh before invoking `serverCall()`/`answer()`.
  useEffect(() => {
    const refreshIfStale = () => {
      const previous = currentSessionId;
      if (!previous || currentRefreshPromise) {
        return;
      }
      const { timeSinceLastSessionCreateMs } = getVonageSessionDiagnostics();
      if (
        timeSinceLastSessionCreateMs === null ||
        timeSinceLastSessionCreateMs < SESSION_TTL_HEARTBEAT_THRESHOLD_MS
      ) {
        return;
      }

      refreshVonageClient({
        trigger: { type: 'ttl-heartbeat', previousSessionId: previous },
      }).catch((error) => {
        logger.warn('[VonageClientProvider] TTL heartbeat refresh failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
    };

    const interval = setInterval(refreshIfStale, SESSION_TTL_HEARTBEAT_INTERVAL_MS);

    // Interval may not fire in time when the tab was deeply throttled or the
    // machine was asleep. Cover that by re-checking on tab return.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshIfStale();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    const onVonageSessionError = client.on('sessionError', async (reason) => {
      logger.error('[VonageClientProvider] Session Error', { reason });
      lastSessionErrorRef.current = { reason, timestamp: Date.now() };

      const diagnostics = getCallDiagnosticsSync();
      log.warn('Vonage session error', {
        reason,
        timestamp: Date.now(),
        sessionId: currentSessionId,
        diagnostics,
      });
      tracingService.trackEvent(CallEventName.VonageSessionError, CallEventLevel.Error, {
        errorMessage: reason,
        reason,
        sessionId: currentSessionId,
      });

      // All three SDK-raised `SessionErrorReason` values require an app-level
      // resume. Previously only EXPIRED_TOKEN was handled, which meant
      // TRANSPORT_CLOSED / PING_TIMEOUT would leave the app in a broken state
      // with no recovery attempt. Each of the three now has its own trigger
      // type so the telemetry is distinguishable in NR.
      const sessionFatalTrigger: Record<
        typeof reason,
        'expired-token' | 'transport-closed' | 'ping-timeout' | null
      > = {
        EXPIRED_TOKEN: 'expired-token',
        TRANSPORT_CLOSED: 'transport-closed',
        PING_TIMEOUT: 'ping-timeout',
      };
      const triggerType = sessionFatalTrigger[reason] ?? null;

      if (!triggerType) {
        return;
      }

      // If we somehow received a session-fatal error without ever having
      // had a session, fall back to an initial-boot create — the SDK
      // contract doesn't let us resume without an id.
      const previous = currentSessionId;
      const trigger: SessionTrigger = previous
        ? { type: triggerType, previousSessionId: previous }
        : { type: 'initial-boot' };

      const notifySessionErrorHandlers = async () => {
        const handlers = sessionErrorHandlersRef.current;
        const handlerCount = handlers.size;
        logger.info(
          '[VonageClientProvider] Notifying session error handlers after failed recovery',
          {
            handlerCount,
            reason,
          },
        );

        for (const handler of handlers) {
          try {
            await handler();
          } catch (error) {
            logger.error('[VonageClientProvider] Session error handler failed', {
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      };

      // Refresh first; only enter the reconnect grace (and eventually run
      // cleanup handlers) if that refresh itself fails. Clearing the
      // reducer before a successful refresh makes pending outbound calls
      // look like normal no-answer terminations.
      withRefreshBarrier(() => performSessionRefresh(trigger))
        .then((newSessionId) => {
          logger.info('[VonageClientProvider] Session auto-refreshed after session error', {
            reason,
            triggerType,
            newSessionId,
            previousSessionId: previous,
          });
        })
        .catch((error) => {
          logger.error('[VonageClientProvider] Auto-refresh failed, entering reconnect grace', {
            reason,
            triggerType,
            windowMs: SESSION_RECONNECT_WINDOW_MS,
            error: error instanceof Error ? error.message : String(error),
          });
          enterReconnectGrace(
            () => {
              const latestPrevious = currentSessionId;
              const retryTrigger: SessionTrigger = latestPrevious
                ? { type: triggerType, previousSessionId: latestPrevious }
                : { type: 'initial-boot' };
              return withRefreshBarrier(() => performSessionRefresh(retryTrigger));
            },
            notifySessionErrorHandlers,
            {
              reason,
              triggerType,
              previousSessionId: previous ?? null,
              getCallIdAtEntry: () => getVonageActiveCallId(),
            },
          );
        });
    });

    // Listen for callMediaDisconnect which might indicate connection issues
    const onCallMediaDisconnect = client.on('callMediaDisconnect', (callId, reason) => {
      logger.warn('[VonageClientProvider] Media Disconnect', { callId, reason });
      const mediaDisconnectDiagnostics = getCallDiagnosticsSync();
      log.warn('Vonage media disconnect', {
        callId,
        reason,
        timestamp: Date.now(),
        diagnostics: mediaDisconnectDiagnostics,
      });
      tracingService.trackEvent(CallEventName.StatusDisconnected, CallEventLevel.Info, {
        deviceLegId: callId,
        errorMessage: reason,
      });
    });

    return () => {
      // Session lifetime is owned by the module (getVonageClient /
      // resetVonageClient), not by React's component tree. We MUST NOT call
      // `client.deleteSession()` here: in dev, React.StrictMode fires this
      // cleanup immediately after the first mount, which kills the freshly
      // created session — the next `serverCall()` then fails with
      // `session:error:no-active-session`.
      //
      // Real cleanup (logout, user-change) is handled by explicit
      // `resetVonageClient(reason)` calls — see specs/vonage-client-lifetime.md.
      // Listeners are still detached so StrictMode's second mount doesn't
      // stack duplicate handlers.
      client.off('sessionError', onVonageSessionError);
      if (onCallMediaDisconnect) {
        client.off('callMediaDisconnect', onCallMediaDisconnect);
      }
    };
  }, [client]);

  const values = useMemo(
    () => ({
      client,
      sessionId,
      isAvailable: !!sessionId, // Calling features available only when session exists
      refreshSession: async () => {
        const handlers = sessionErrorHandlersRef.current;
        const handlerCount = handlers.size;
        const diagnostics = getCallDiagnosticsSync();

        log.info('Session refresh requested', {
          sessionId: currentSessionId,
          handlerCount,
          timestamp: Date.now(),
          diagnostics,
        });

        // Wrap handlers + refresh under the same barrier. The barrier
        // publishes `currentRefreshPromise` synchronously before work
        // starts, so during the handler loop any concurrent refresh source
        // (heartbeat, sessionError) dedups on our in-flight promise
        // instead of slipping past the null check and creating a new
        // session while our cleanup is still running against the old one.
        await withRefreshBarrier(async () => {
          // Notify handlers before refresh to cleanup call state. Session
          // refresh clears Vonage SDK's call registry, so app state must be
          // cleared too to prevent "No call found" errors downstream.
          for (const handler of handlers) {
            try {
              await handler();
            } catch (error) {
              logger.error('[VonageClientProvider] Session refresh handler failed', {
                error: error instanceof Error ? error.message : String(error),
              });
            }
          }

          // External callers (voice hooks, banner retry) don't carry a
          // session-fatal reason. Tag as `manual-reset` with a generic
          // reason so these calls stand out in telemetry; specific call-sites
          // (banner retry, voice hooks) provide their own narrower reason.
          return performSessionRefresh({
            type: 'manual-reset',
            reason: 'external-refresh',
          });
        });
      },
      isConnectionBlocked: connectionBlocked,
      resetConnection: () => {
        sessionManager.resetConnection();
        setConnectionBlocked(false);
      },
      registerSessionErrorHandler: (handler: SessionErrorHandler) => {
        sessionErrorHandlersRef.current.add(handler);

        // Return cleanup function
        return () => {
          sessionErrorHandlersRef.current.delete(handler);
          const connectionState = sessionManager.getConnectionState();
          const diagnostics = getCallDiagnosticsSync();
          logger.info('[VonageClientProvider] Unregistered session error handler', {
            totalHandlers: sessionErrorHandlersRef.current.size,
            sessionId: currentSessionId,
            lastSessionError: lastSessionErrorRef.current,
            connectionState: {
              isBlocked: connectionState.isBlocked,
              failures: connectionState.failures,
              lastFailureTime: connectionState.lastFailureTime,
            },
            diagnostics,
            stack: new Error().stack,
          });
        };
      },
    }),
    [client, connectionBlocked, sessionId],
  );

  return <VonageClientContext.Provider value={values}>{children}</VonageClientContext.Provider>;
};

export const useVonageClient = () => {
  const context = use(VonageClientContext);

  if (!context) {
    throw new Error('useVonageClient must be used within a VonageClientProvider');
  }
  if (!context.client) {
    throw new Error('useVonageClient must be used within a VonageClientProvider');
  }
  return context.client;
};

export const useVonageSession = () => {
  const context = use(VonageClientContext);

  if (!context) {
    throw new Error('useVonageSession must be used within a VonageClientProvider');
  }

  return {
    sessionId: context.sessionId,
    isAvailable: context.isAvailable,
    refreshSession: context.refreshSession,
  };
};

/**
 * Do the actual create/refresh work. Does not manage `currentRefreshPromise`
 * — callers must run this through `withRefreshBarrier` (or the public
 * `refreshVonageClient` wrapper) so dedup and `ensureSessionReady()` work.
 */
const performSessionRefresh = async (trigger: SessionTrigger): Promise<string | undefined> => {
  logger.info('[VonageClientProvider] Attempting Vonage session operation...', {
    triggerType: trigger.type,
    hasExistingSessionId: 'previousSessionId' in trigger,
    timestamp: new Date().toISOString(),
  });

  // region/userId/businessId are not caller-provided. They come from the
  // module-level user context (set by the loader on every run via
  // `setVonageUserContext`). Reading from the context unifies loader- and
  // provider-initiated refreshes (EXPIRED_TOKEN handler, external
  // `refreshSession()`) so error reports always carry user/business/region.
  const ctx = getVonageUserContext();
  const client = getVonageClient({ rtcStatsTelemetry: true });

  const newSessionId = await sessionManager.createOrRefreshSession(client, getToken, {
    trigger,
    region: ctx?.region,
    userId: ctx?.userId,
    businessId: ctx?.businessId ?? undefined,
  });

  // Module state is the single source of truth. Every successful
  // create/resume updates it; subscribers (the provider via
  // useSyncExternalStore) re-render.
  setCurrentSessionId(newSessionId);
  // Track the create so `getVonageSessionDiagnostics()` can report
  // sessionsSinceCallStart + timeSinceLastSessionCreate when a phantom is
  // detected downstream.
  if (newSessionId) {
    recordSessionCreated();
  }

  return newSessionId;
};

/**
 * Publish `currentRefreshPromise` for the lifetime of `work`. If another
 * refresh path (heartbeat, external refreshSession, session-error handler,
 * media-timeout burst) is already in flight, dedup by awaiting it and
 * returning the session id it produced.
 *
 * Callers can compose pre-refresh steps (e.g. running cleanup handlers) inside
 * `work` — those steps run under the same barrier, so another refresh source
 * cannot slip past the `currentRefreshPromise === null` check while the caller
 * is mid-cleanup. This is why `refreshSession` and the `sessionError` listener
 * go through `withRefreshBarrier` directly instead of calling `refreshVonageClient`:
 * it lets them fold handler loops into the barrier without self-awaiting.
 */
export const withRefreshBarrier = async (
  work: () => Promise<string | undefined>,
): Promise<string | undefined> => {
  // Dedup against an already-publishing caller. If the prior refresh
  // succeeded, reuse its session id — the caller's own work (if any) is
  // redundant. If the prior refresh failed (e.g. resume of an expired
  // session → 401), fall through and run our own `work()`: inferring success
  // from `currentSessionId` being truthy would silently return the stale id
  // the failed resume targeted and the next serverCall/answer would trip on
  // `no-active-session`.
  if (currentRefreshPromise) {
    const prior = await currentRefreshPromise;
    if (prior.ok) {
      return prior.sessionId;
    }
  }

  // Wrap the work so the barrier promise resolves to an outcome (never
  // rejects) — `ensureSessionReady` only needs to know when the barrier
  // settles, and dedup callers read `prior.ok` above to decide whether
  // to reuse or retry.
  const operation: Promise<RefreshOutcome> = work().then(
    (sessionId) => ({ ok: true, sessionId }) as RefreshOutcome,
    (error) => ({ ok: false, error }) as RefreshOutcome,
  );
  setCurrentRefreshPromise(operation);
  try {
    const outcome = await operation;
    if (outcome.ok) {
      return outcome.sessionId;
    }
    throw outcome.error;
  } finally {
    setCurrentRefreshPromise(null);
  }
};

export const refreshVonageClient = async ({
  trigger,
}: {
  trigger: SessionTrigger;
}): Promise<string | undefined> => {
  // Short-circuit: an `initial-boot` request with an existing session is a
  // redundant call from a loader re-run (router.invalidate, auth flow,
  // impersonation). Returning the existing id stops the loader from rotating
  // the Vonage session on every invalidation.
  // Checked before the barrier because it's a pure read with no side effects.
  if (trigger.type === 'initial-boot' && currentSessionId) {
    logger.info('[VonageClientProvider] initial-boot requested but session exists, reusing', {
      sessionId: currentSessionId,
    });
    return currentSessionId;
  }
  return withRefreshBarrier(() => performSessionRefresh(trigger));
};

const getToken = async (): Promise<string> => {
  const tokenFetchStart = Date.now();

  try {
    const voipResponse = await $fetchAllo.POST('/v1/app/voip/auth');
    const tokenFetchDuration = Date.now() - tokenFetchStart;

    if (isApiError(voipResponse.error) && voipResponse.error.code === 'BLOCKED_USER') {
      throw new BlockedUserError();
    }

    const token = voipResponse.data?.data?.token;

    if (!token) {
      const error = new Error('VoIP token missing in response');
      logger.error('[VonageClientProvider] Token missing in API response', {
        response: voipResponse.data,
      });
      throw error;
    }

    logger.info(`[VonageClientProvider] VoIP token fetched in ${tokenFetchDuration}ms`, {
      duration: tokenFetchDuration,
    });
    return token;
  } catch (error) {
    if (error instanceof Error && error.message === 'VoIP token missing in response') {
      throw error;
    }

    logger.error('[VonageClientProvider] Token fetch failed', {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};
